(ns portfolio.views
  (:require [clojure.string :as str]
            [re-frame.core :as rf]
            [portfolio.content :as c]
            [portfolio.events :as events]
            [portfolio.i18n :as i18n :refer [tr]]
            [portfolio.subs :as subs]))

;; ---------- atomic ----------

(defn chip [label]
  [:span.chip label])

(defn- render-tok [tok]
  (cond
    (string? tok)         tok
    (and (vector? tok)
         (keyword? (first tok)))
    (let [[cls & children] tok]
      (into [:span {:class (name cls)}] (map render-tok children)))
    :else                 (str tok)))

(defn- code-line [parts]
  (into [:div.code-line] (map render-tok parts)))

(defn code-card [{:keys [title body]}]
  [:div.code-card
   [:div.code-card__chrome
    [:span.code-card__dot]
    [:span.code-card__dot]
    [:span.code-card__dot]
    [:span.code-card__title title]]
   [:div.code-card__body
    (for [[i [_ parts]] (map-indexed vector body)]
      ^{:key i} [code-line parts])]])

;; ---------- nav + language toggle ----------

(defn lang-toggle []
  (let [current @(rf/subscribe [::subs/lang])]
    [:div.lang-toggle
     {:role "group" :aria-label "language"}
     (for [l i18n/supported-langs]
       ^{:key l}
       [:button.lang-toggle__btn
        {:type     "button"
         :class    (when (= current l) "lang-toggle__btn--active")
         :on-click #(rf/dispatch [::events/set-lang l])
         :aria-pressed (str (= current l))}
        (get i18n/lang-labels l)])]))

(defn nav []
  (let [active @(rf/subscribe [::subs/active-section])
        link   (fn [href section label]
                 [:a.nav__link
                  {:href  href
                   :class (when (= active section) "nav__link--active")}
                  label])]
    [:nav.nav
     [:div.container
      [:div.nav__inner
       [:a.nav__brand {:href "#top"} "AR"]
       [:div.nav__links
        (link "#about"    :about    (tr (:about c/nav-labels)))
        (link "#projects" :projects (tr (:work c/nav-labels)))
        (link "#contact"  :contact  (tr (:contact c/nav-labels)))
        [:a.nav__link.nav__link--ext
         {:href   (str "https://github.com/" c/github-user)
          :target "_blank"
          :rel    "noopener"}
         (tr (:github c/nav-labels))]
        [lang-toggle]]]]]))

;; ---------- hero ----------

(defn hero []
  (let [{:keys [eyebrow name-first name-last role status chips code-card]} c/hero]
    [:section.hero {:id "top"}
     [:div.container
      [:div.hero__grid
       [:div
        [:p.hero__eyebrow (tr eyebrow)]
        [:h1.hero__name
         name-first " "
         [:span.hero__name-accent name-last]]
        [:p.hero__role (tr role)]
        [:p.hero__status
         [:span.hero__status-dot]
         (tr status)]
        [:div.hero__chips
         (for [c chips] ^{:key c} [chip c])]]
       [:div
        [portfolio.views/code-card code-card]]]]]))

;; ---------- stats strip ----------

(defn- stat-cell [{:keys [value label]}]
  [:div.stat
   [:div.stat__value value]
   [:div.stat__label
    (for [[i line] (map-indexed vector (str/split-lines (tr label)))]
      ^{:key i} [:span.stat__label-line line])]])

(defn stats []
  [:section.stats-band
   [:div.container
    [:div.stats
     (for [s c/stats] ^{:key (:value s)} [stat-cell s])]]])

;; ---------- sections ----------

(defn section-head [{:keys [eyebrow-num title]}]
  (let [t (tr title)]
    [:div.section__head
     [:p.section__eyebrow
      [:span.section__eyebrow-num eyebrow-num]
      "— " (str/lower-case t)]
     [:h2.section__title t]]))

(defn how-i-work-card [{:keys [heading body]}]
  [:div.how-card
   [:h3.how-card__heading [:span.how-card__caret "›"] " " (tr heading)]
   [:p.how-card__body (tr body)]])

(defn how-i-work []
  (let [{:keys [section cards footer]} c/how-i-work]
    [:section.section {:id "about"}
     [:div.container
      [section-head section]
      [:div.how-grid
       (for [c cards] ^{:key (-> c :heading (get :en))} [how-i-work-card c])]
      [:p.how-background (tr footer)]]]))

(defn- project-preview [lines]
  [:div.project-card__preview
   (for [[i line] (map-indexed vector lines)]
     ^{:key i} [:div.code-line line])])

(defn project-card [{:keys [title tag desc preview stack repo live]}]
  [:article.project-card
   [:div.project-card__head
    [:h3.project-card__title title]
    (when tag [:span.project-card__tag (tr tag)])]
   [:p.project-card__desc (tr desc)]
   (when preview [project-preview preview])
   [:div.project-card__stack
    (for [s stack] ^{:key s} [chip s])]
   [:div.project-card__links
    (when repo [:a {:href repo :target "_blank" :rel "noopener"}
                (tr {:en "source ↗" :pt "código ↗"})])
    (when live [:a {:href live :target "_blank" :rel "noopener"}
                (tr {:en "live ↗" :pt "demo ↗"})])]])

(defn projects []
  [:section.section {:id "projects"}
   [:div.container
    [section-head c/projects-section]
    [:div.projects
     (for [p c/projects] ^{:key (:title p)} [project-card p])]]])

(defn contact-item [{:keys [label href text]}]
  [:li.contact-item
   [:div.contact-item__label label]
   [:a {:href href :target "_blank" :rel "noopener"} text]])

(defn contact []
  (let [{:keys [opener] :as section} c/contact-section]
    [:section.section {:id "contact"}
     [:div.container
      [section-head (dissoc section :opener)]
      (when opener [:p.contact__opener (tr opener)])
      [:ul.contact-list
       (for [c c/contact] ^{:key (:label c)} [contact-item c])]]]))

(defn footer []
  (let [{:keys [owner-name prompt source-link repo-url]} c/footer
        year (.getFullYear (js/Date.))]
    [:footer.footer
     [:div.container
      [:div.footer__row
       [:span.footer__prompt (tr prompt)]
       [:span "© " year " " owner-name " · "
        [:a {:href repo-url :target "_blank" :rel "noopener"} (tr source-link)]]]]]))

(defn app []
  [:div.app
   [nav]
   [:main
    [hero]
    [stats]
    [how-i-work]
    [projects]
    [contact]]
   [footer]])
